/**
 * src/pages/WhereIsEverythingGoing/sections/WhyItMattersSection.tsx
 * Reflective closing section: "Why it matters now" (philosophy label)
 * connecting deep time to the other three questions:
 * - Where Did We Come From? (cosmic nucleosynthesis & chemical origins)
 * - What Is Consciousness? (the fragile observer giving meaning to the cosmos)
 * - What Happens After Death? (entropy, personal impermanence, and cosmic scale)
 */

import React from "react";
import { EvidenceBadge } from "@/components/originBeyond/EvidenceBadge";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Link } from "@/components/common/Link";
import { Sparkles, Compass, Heart, Atom, Brain, Moon, ArrowRight } from "lucide-react";
export function WhyItMattersSection() {
  return <section id="why-it-matters" className="w-full max-w-full min-w-0 py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-t border-border/30" aria-labelledby="why-it-matters-heading">
      <div className="max-w-[76rem] mx-auto min-w-0">
        <SectionHeading eyebrow="Cosmic Perspective & Epistemology" title="Why It Matters Now" description="Contemplating trillions of years is not an escape from our fragile present—it is the ultimate lens for understanding who we are and what we must protect." />

        <div className="ql-glass-strong p-6 sm:p-8 lg:p-10 rounded-2xl border border-primary/40 flex flex-col gap-8 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-border/30">
            <div className="flex items-center gap-2">
              <Sparkles className="size-4 text-primary shrink-0" aria-hidden="true" />
              <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold">
                Philosophical Synthesis
              </span>
            </div>
            <EvidenceBadge level="philosophy" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start min-w-0">
            {/* Main Narrative Column */}
            <div className="lg:col-span-7 space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed font-sans min-w-0">
              <p>
                When we look across the quadrillions of years separating our era from the
                decline of stars, the overwhelming truth is not cosmic nihilism, but the
                extraordinary rarity of the present moment. We inhabit the{" "}
                <strong className="text-foreground">Stelliferous Era</strong>—the brief,
                vibrant window when stars shine, heavy elements organize into organic
                molecules, and conscious observers look up into a transparent night sky.
              </p>
              <p>
                In a hundred billion years, metric acceleration will carry distant galaxies
                beyond our observable horizon. Any future civilization born then would
                perceive an isolated, static island universe, blind to cosmic expansion or
                the cosmic microwave background. We are privileged witnesses to the full
                tapestry of cosmology.
              </p>
              <p>
                Recognizing the transience of all physical structures gives urgency to our
                immediate ethical choices. How we steward this single planet, resolve our
                existential hazards, and protect conscious awareness is the only chapter of
                cosmic history we have the power to write.
              </p>
            </div>

            {/* Connecting Bridge to Other Three Questions */}
            <div className="lg:col-span-5 flex flex-col gap-3 min-w-0">
              <span className="font-mono text-xs text-primary font-semibold uppercase tracking-wider mb-1">
                The Four Pillars of Inward &amp; Outward Inquiry
              </span>

              {/* Link to Q1: Origin */}
              <Link to="/WhereDidWeComeFromPage" className="p-3.5 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all flex items-center justify-between group text-foreground no-underline min-w-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="size-8 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-primary shrink-0">
                    <Atom className="size-4" />
                  </div>
                  <div className="min-w-0">
                    <span className="font-mono text-[10px] text-primary uppercase block">
                      Question 01 • Origin
                    </span>
                    <span className="font-serif text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate block">
                      Where Did We Come From?
                    </span>
                  </div>
                </div>
                <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0 ml-2" />
              </Link>

              {/* Link to Q2: Consciousness */}
              <Link to="/WhatIsConsciousnessPage" className="p-3.5 rounded-xl bg-card/50 border border-border/50 hover:border-blue-400/50 transition-all flex items-center justify-between group text-foreground no-underline min-w-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="size-8 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400 shrink-0">
                    <Brain className="size-4" />
                  </div>
                  <div className="min-w-0">
                    <span className="font-mono text-[10px] text-blue-400 uppercase block">
                      Question 02 • Awareness
                    </span>
                    <span className="font-serif text-sm font-semibold text-foreground group-hover:text-blue-400 transition-colors truncate block">
                      What Is Consciousness?
                    </span>
                  </div>
                </div>
                <ArrowRight className="size-4 text-muted-foreground group-hover:text-blue-400 group-hover:translate-x-1 transition-all shrink-0 ml-2" />
              </Link>

              {/* Link to Q3: Death */}
              <Link to="/WhatHappensAfterDeathPage" className="p-3.5 rounded-xl bg-card/50 border border-border/50 hover:border-purple-400/50 transition-all flex items-center justify-between group text-foreground no-underline min-w-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="size-8 rounded-full bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-300 shrink-0">
                    <Moon className="size-4" />
                  </div>
                  <div className="min-w-0">
                    <span className="font-mono text-[10px] text-purple-300 uppercase block">
                      Question 03 • Mortality
                    </span>
                    <span className="font-serif text-sm font-semibold text-foreground group-hover:text-purple-300 transition-colors truncate block">
                      What Happens After Death?
                    </span>
                  </div>
                </div>
                <ArrowRight className="size-4 text-muted-foreground group-hover:text-purple-300 group-hover:translate-x-1 transition-all shrink-0 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}